/* TokenRow.js */

import React, { useState } from "react";
import { IconButton, Tooltip, Fab } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import "./TokenRow.css";

const TokenRow = ({ 
  colorVariant, 
  token, 
  url, 
  isSelected, 
  isSelectable, 
  onSelect, 
  tokenType,
  onCopyToken,
  onOpenUrl,
  darkTooltipSx
}) => {
  const [actionsOpen, setActionsOpen] = useState(false);

  const pillClass = [
    "alcTokenPill",
    `tone-${colorVariant}`,
    isSelectable ? "isSelectable" : "isStatic",
    isSelected ? "isSelected" : "",
    actionsOpen ? "hasActionsOpen" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const safeToken = token || "";

  const canCopyToken = !!safeToken;
  const canOpenUrl = !!url;
  const canCopyUrl = !!url;

  const handleRootClick = isSelectable
    ? () => {
        if (actionsOpen) return;
        onSelect?.();
      }
    : undefined;

  const toggleActions = (event) => {
    event.stopPropagation();
    setActionsOpen((wasOpen) => !wasOpen);
  };

  const closeActions = (event) => {
    event?.stopPropagation?.();
    setActionsOpen(false);
  };

  const executeActionAndCloseMenu = (event, actionCallback) => {
    event.stopPropagation();
    actionCallback?.();
    setActionsOpen(false);
  };

  const flyoutClass = ["alcChipFlyout", actionsOpen ? "isOpen" : ""].filter(Boolean).join(" ");

  return (
    <div
      className={pillClass}
      onClick={handleRootClick}
      role={isSelectable ? "button" : undefined}
      aria-pressed={isSelectable ? !!isSelected : undefined}
      tabIndex={isSelectable ? 0 : -1}
      onKeyDown={
        isSelectable
          ? (event) => {
              if (actionsOpen) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect?.();
              }
            }
          : undefined
      }
    >
      <span className="alcTokenText">{safeToken || "UNKNOWN"}</span>

      <span className="alcChipActionsHost" onClick={(event) => event.stopPropagation()}>
        <span className={flyoutClass} onClick={(event) => event.stopPropagation()}>
          <Tooltip title="Copy token" componentsProps={{ tooltip: { sx: darkTooltipSx } }}>
            <span>
              <IconButton
                size="small"
                className="alcChipActionBtn"
                aria-label={`Copy ${tokenType} token`}
                disabled={!canCopyToken}
                disableRipple
                onClick={(event) => 
                  executeActionAndCloseMenu(event, () => onCopyToken(safeToken, `${tokenType} token copied`))
                }
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Open URL" componentsProps={{ tooltip: { sx: darkTooltipSx } }}>
            <span>
              <IconButton
                size="small"
                className="alcChipActionBtn"
                aria-label={`Open ${tokenType} URL`}
                disabled={!canOpenUrl}
                disableRipple
                onClick={(event) => executeActionAndCloseMenu(event, () => onOpenUrl(url))}
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Copy URL" componentsProps={{ tooltip: { sx: darkTooltipSx } }}>
            <span>
              <IconButton
                size="small"
                className="alcChipActionBtn"
                aria-label={`Copy ${tokenType} URL`}
                disabled={!canCopyUrl}
                disableRipple
                onClick={(event) => 
                  executeActionAndCloseMenu(event, () => onCopyToken(url, `${tokenType} URL copied`))
                }
              >
                <LinkIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </span>

        <Fab size="small" className="alcChipFab" aria-label={`${tokenType} actions`} onClick={toggleActions}>
          {actionsOpen ? <CloseIcon fontSize="inherit" /> : <SettingsIcon fontSize="inherit" />}
        </Fab>

        {actionsOpen && (
          <span
            className="alcChipFlyoutBackdrop"
            onClick={closeActions}
            role="button"
            tabIndex={-1}
            aria-hidden="true"
          />
        )}
      </span>
    </div>
  );
};

export default TokenRow;
